import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui';
import { MessageChannel } from '../../types';

interface MessageInputProps {
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
  channel?: MessageChannel;
}

const SMS_SEGMENT_LENGTH = 160;

const MessageInput: React.FC<MessageInputProps> = ({
  onSend,
  disabled = false,
  placeholder = 'Type a message...',
  channel
}) => {
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [text]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (text.trim() && !disabled) {
      onSend(text);
      setText('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const smsSegments = channel === 'sms' ? Math.ceil(text.length / SMS_SEGMENT_LENGTH) || 1 : 0;

  return (
    <form
      onSubmit={handleSubmit}
      className="p-4 border-t border-secondary-200 bg-white"
    >
      <div className="flex items-end gap-3">
        {/* Text input */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            className={`
              w-full px-4 py-3 border border-secondary-300 rounded-2xl resize-none
              focus:ring-2 focus:ring-primary-500 focus:border-transparent
              disabled:bg-secondary-50 disabled:cursor-not-allowed
            `}
            style={{ maxHeight: '150px' }}
          />
        </div>

        {/* Send button */}
        <Button
          type="submit"
          disabled={disabled || !text.trim()}
          className="h-12 w-12 p-0 rounded-full flex-shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </Button>
      </div>

      <div className="flex items-center justify-between mt-2">
        <p className="text-xs text-secondary-400">
          Press Enter to send, Shift+Enter for new line
        </p>
        {channel === 'sms' && text.length > 0 && (
          <p className={`text-xs ${text.length > SMS_SEGMENT_LENGTH ? 'text-warning-600' : 'text-secondary-400'}`}>
            {text.length}/{SMS_SEGMENT_LENGTH} chars
            {smsSegments > 1 && ` (${smsSegments} segments)`}
          </p>
        )}
      </div>
    </form>
  );
};

export default MessageInput;
